import axios from "axios"
import { enqueueSnackbar } from "notistack"
import { IPaymentDetailsPageData, IReceivableDetails } from "pages/Projects/ProjectServices"
import { api } from "../../../../api"
import { PresignedUrl, submitFinalUploads } from "../../../../utils/utils"
import {
    BOQSource,
    HistoryData,
    IFile,
    IOtherDetailItem,
    IResponse,
    ProjectCategory,
    StatusButton,
    TicketDataResponseType,
} from "../../../Common/commonTypes"

export type SurveyBOQItemsPayload = {
    product_id: string | number
    quantity: number
    client_cost: number
    total_cost: number
    source: BOQSource
    remarks?: string
    item_id?: number
}

const MODULE_URL = "operations/survey"
const MODULE_URL_2 = "surveys"

type SurveyRelatedTickets = {
    project_id: number
    ticket_id: number
    tickets: IOtherDetailItem[]
    total: number
    msrd_enabled: boolean
}

export type SurveyContractor = {
    vts_id: number
    vendor_id: number
    vendor_name: string | null
    assigned_at: string | null
    assigned_by: string | null
    acceptance_status: string | null
    acceptance_date: string | null
    unique_code: string | null
    status: string | null
    vendor_representative: string | null
    contact_number: string | null
    executive_id: number | null
    sow_groups?: { id: number; name: string }[]
}

export type VendorContact = {
    id: number
    name: string
    phone?: string | null
    email?: string | null
    country_code?: string | null
}
export type SurveyTabData = {
    status: string
    status_id: number[]
    status_key: string
    ticket_count: number
    total_pages: number
}

export type SurveyTabsResponse = {
    data: {
        message: SurveyTabData[]
        status_code: number
        timestamp: string
    }
}

type EditImageRequest = {
    ticket_id: string
    removed_ids: number[]
    files: {
        file_type: string
        file_name: any
    }[]
}

type EditImageResponse = {
    message: string
    removed_doc_ids: number[]
    urls: { urls: PresignedUrl[]; file_type: string }[]
}

export type SurveyDetailsAddress = {
    address: string | null
    city: string | null
    city_id: number | null
    coordinates: {
        lat: number | null
        lng: number | null
    }
    country: string | null
    country_id: number | null
    pincode: string | null
    site_display_id: string | null
    site_id: number | null
    state: string | null
    state_id: number | null
}

export type SurveyChargerItem = {
    id: string
    power_rating: number | null
    quantity: number | null
    type: string | null
}

export type SurveyChargerDetails = {
    ac_count: number
    chargers: SurveyChargerItem[]
    dc_count: number
    total: number
}

export type SurveyHistoryEntry = {
    updated_at?: string | null
    updated_by?: string | null
    value?: string | null
    reason?: string | null
    [key: string]: any
}

export type SurveyKeyDateField = {
    history: SurveyHistoryEntry[]
    value: string | null
    reason?: string | null
}

export type SurveyKeyDates = {
    created_by: {
        id: number | null
        name: string | null
    }
    planned_survey_date: SurveyKeyDateField
    requested_closure_date: SurveyKeyDateField
    tentative_closure_date: SurveyKeyDateField
}

export type SurveyOnHoldSummary = {
    on_hold: boolean
    reason?: string | null
    on_hold_since?: string | null
    [key: string]: any
}

export type SurveyOtherDetails = {
    available_load: number | null
    connection_owned_by: string | null
    connection_type: string | null
    discom_name: string | null
    foundation_type: string | null
    number_of_spots: number | null
    other_images: Array<{ id?: number; view_url?: string; [key: string]: any }>
    remarks: string | null
    spot_images: Array<{ id?: number; view_url?: string; [key: string]: any }>
    vin_number: string | null
}

export type SurveyOverview = {
    assigned_to: string | null
    category: ProjectCategory
    deal_type: string | null
    ticket_type: string | null
    time_slot: string | null
    vendor_name: string | null
}

export type SurveyCustomerPoc = {
    contact_no: string | null
    customer_name: string | null
    email: string | null
    id: number | null
}

export type SurveyOnGroundPoc = {
    contact_no: string | null
    email: string | null
    id: number | null
    name: string | null
}

export type SurveyPocs = {
    customer: SurveyCustomerPoc[]
    on_ground: SurveyOnGroundPoc[]
}

export type SurveyProgress = {
    completed_date: string | null
    created_at: string | null
    dropped_date: string | null
    percentage: number
    start_date: string | null
}

export type SurveyTicket = {
    display_id: string
    id: number
    project_id: number | null
    project_name: string | null
    status: string | null
    status_color: string | null
    status_id: number | null
    text_color: string | null
    type: string | null
}

export type SurveyDetailsResponse = {
    address: SurveyDetailsAddress
    age: string | null
    charger_details: SurveyChargerDetails
    key_dates: SurveyKeyDates
    on_hold_summary: SurveyOnHoldSummary
    other_details: SurveyOtherDetails
    overview: SurveyOverview
    pocs: SurveyPocs
    progress: SurveyProgress
    ticket: SurveyTicket
    tags?: { primary: any[]; others: any[] }
}
const buildSurveyQueryParams = (data: any, page?: number, per_page?: number) => ({
    state_id: JSON.stringify(data.stateId ?? []),
    child_status_ids: JSON.stringify(data.statusId ?? []),
    site_ids: JSON.stringify(data.siteIds ?? []),
    city_id: JSON.stringify(data.cityId ?? []),
    country_ids: JSON.stringify(data.country_id?.id ? [data.country_id.id] : []),
    tag_ids: JSON.stringify(data.tags ?? []),
    category_ids: JSON.stringify((data.category_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))),
    blocked_reason_id: JSON.stringify(data.blocked_reason_id ?? []),
    assigned_to: data.assigned_to?.id ?? "",
    age: JSON.stringify(data.age ?? []),
    age_op: data.age_op ?? "",
    deal_type: data.dealType ?? "",
    charger_type: JSON.stringify(data.chargerIds ?? []),
    power_rating: JSON.stringify(data.powerRatingIds ?? []),
    search_key: data.search_key ?? "",
    read_filter: data.ReadUnread ?? "all",
    raised_on: data.added_from || data.added_to ? "" : (data.added_on?.from ?? ""),
    raised_on_from: data.added_from ?? "",
    raised_on_to: data.added_to ?? "",
    vendor_ids: JSON.stringify(data.vendor_ids ?? []),
    progress: JSON.stringify(
        [data.progress_from, data.progress_to].filter(v => v !== "" && v !== undefined && v !== null).map(Number)
    ),
    progress_op: data.progress_op?.id ?? "",
    on_hold: data?.on_hold?.id === 1 ? "yes" : data?.on_hold?.id === 0 ? "no" : "",
    on_hold_reason_ids: JSON.stringify(data.on_hold_reason_ids ?? []),
    on_hold_date: data.on_hold_date ?? "",
    type: data.ticketType ?? "all",
    planned_survey_date_start: data.PlannedStartDate ?? "",
    planned_survey_date_end: data.PlannedEndDate ?? "",
    account_ids: JSON.stringify(data.account_ids ?? []),
    // ops_estimate_approval: data.ops_estimate_approval ?? "",
    // sales_estimate_approval: data.sales_estimate_approval ?? "",
    site_feasibility_status: data.ops_feasibility_status ?? "",
    dfc_approval_status: JSON.stringify((data.dfc_approval_status ?? []).map((o: any) => o.id)),
    region_ids: JSON.stringify(data.zone_ids ?? []),
    contractor_unassigned: data.contractor_assigned === "no" ? true : "",
    ...(page !== undefined && { page }),
    ...(per_page !== undefined && { per_page }),
})

const handleSnackbar = async (
    queryFulfilled: any,
    successMsg: (data: any) => string,
    fallbackSuccess = "Success",
    fallbackError = "Something went wrong"
) => {
    try {
        const { data } = await queryFulfilled
        enqueueSnackbar(successMsg(data) || fallbackSuccess, { variant: "success" })
    } catch (error: any) {
        enqueueSnackbar(error?.error?.data?.message || error?.error?.data?.error || error?.message || fallbackError, {
            variant: "error",
        })
    }
}
const surveyService = api.injectEndpoints({
    endpoints: build => ({
        // Queries
        getSurveyTabs: build.query({
            query: (params: { data: any; page: number; selectedValue: number }) => ({
                url: `${MODULE_URL_2}/total`,
                params: buildSurveyQueryParams(params.data, params.page, params.selectedValue),
                useV3: true,
            }),
            transformResponse: (response: SurveyTabsResponse) => response.data.message,
            providesTags: ["Survey"],
        }),
        getSurveyData: build.query<TicketDataResponseType, { data: any; page: number; selectedValue: number }>({
            query: params => ({
                url: `${MODULE_URL_2}/${params.data.parent_status ?? "all_tab"}`,
                params: buildSurveyQueryParams(params.data, params.page, params.selectedValue),
                useV3: true,
            }),
            providesTags: ["Survey"],
        }),
        getSurveyTicketDetails: build.query<IResponse<any>, { ticket_id: number | string }>({
            query: params => ({
                url: `${MODULE_URL}/ticket_details`,
                params: params,
            }),
            providesTags: (result, error, arg) => ["Survey"],
        }),
        getSurveyDetails: build.query<SurveyDetailsResponse, { surveyId: string | number }>({
            query: ({ surveyId }) => ({
                url: `${MODULE_URL_2}/${surveyId}/details`,
                useV3: true,
            }),
            providesTags: ["Survey"],
        }),
        getSurveyStatusDropdown: build.query({
            query: ({ surveyId }) => ({
                url: `${MODULE_URL_2}/${surveyId}/button_dropdown`,
                useV3: true,
            }),
            providesTags: ["Survey"],
        }),
        getSurveyContractors: build.query<SurveyContractor[], { surveyId: string | number }>({
            query: ({ surveyId }) => ({
                url: `${MODULE_URL_2}/${surveyId}/contractors`,
                useV3: true,
            }),
            providesTags: (_result, _error, arg) => [{ type: "Survey", id: `contractors_${arg.surveyId}` }],
        }),
        getVendorContacts: build.query<{ data: VendorContact[] }, { vendor_id: number | string; ctype_mod?: string }>({
            query: ({ vendor_id, ctype_mod }) => ({
                url: `contacts`,
                params: { ref_id: vendor_id, ctype_mod },
                useV3: true,
            }),
        }),
        getSurveySalesDetails: build.query<{ receivable_details: IReceivableDetails }, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL_2}/sales_details`,
                params: {
                    ticket_id: ticket_id,
                },
                useV3: true,
            }),
            transformResponse: (response: { data: { receivable_details: IReceivableDetails }; success: boolean }) => {
                return response.data
            },
            providesTags: ["Survey"],
        }),
        addSurveyContractor: build.mutation<
            any,
            { surveyId: string | number; vendor_id: number; assigned_by: number; deactivate_existing?: boolean }
        >({
            query: ({ surveyId, vendor_id, assigned_by, deactivate_existing }) => ({
                url: `${MODULE_URL_2}/${surveyId}/contractors`,
                method: "POST",
                body: { vendor_id, assigned_by, deactivate_existing },
                useV3: true,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                await handleSnackbar(queryFulfilled, d => d?.data?.msg)
            },
            invalidatesTags: (_result, _error, arg) => [
                { type: "Survey", id: `contractors_${arg.surveyId}` },
                "Survey",
            ],
        }),
        editSurveyContractor: build.mutation<
            any,
            {
                surveyId: string | number
                vts_id: number
                assigned_by?: number
                is_active?: boolean
                rebuild_vendor_code?: boolean
                deactivate_existing?: boolean
            }
        >({
            query: ({ surveyId, ...body }) => ({
                url: `${MODULE_URL_2}/${surveyId}/contractors`,
                method: "PUT",
                body,
                useV3: true,
            }),
            invalidatesTags: (_result, _error, arg) => [
                { type: "Survey", id: `contractors_${arg.surveyId}` },
                { type: "Survey", id: "BOQ" },
                { type: "Installation", id: "BOQ" },
            ],
        }),
        getSurveyRelatedTickets: build.query<SurveyRelatedTickets[], { surveyId: string | number }>({
            query: ({ surveyId }) => ({
                url: `${MODULE_URL_2}/${surveyId}/tickets`,
                useV3: true,
            }),
            transformResponse: (response: SurveyRelatedTickets | SurveyRelatedTickets[]) =>
                Array.isArray(response) ? response : [response],
        }),
        getPostSurveyDetails: build.query<any, { surveyId: string | number }>({
            query: ({ surveyId }) => ({
                url: `${MODULE_URL_2}/${surveyId}/post_survey_details`,
                useV3: true,
            }),
            providesTags: (_result, _error, arg) => [{ type: "Survey", id: `post_survey_${arg.surveyId}` }],
        }),
        updatePostSurveyDetails: build.mutation<any, { surveyId: string | number; body: FormData }>({
            query: ({ surveyId, body }) => ({
                url: `${MODULE_URL_2}/${surveyId}/post_survey_details`,
                method: "PUT",
                body,
                useV3: true,
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "Survey", id: `post_survey_${arg.surveyId}` }],
        }),
        getSurveyTicketHistory: build.query<HistoryData[], { ticket_id: string }>({
            query: params => ({
                url: `${MODULE_URL_2}/history`,
                params: params,
                useV3: true,
            }),
            providesTags: (result, error, arg) => ["Survey"],
        }),
        getLinkToSurveyList: build.query<any, void>({
            query: () => ({
                url: `active-projects`,
                params: {
                    search_key: "",
                    columns: JSON.stringify(["name", "ops_ticket_id", "display_id"]),
                },
            }),
            providesTags: (result, error, arg) => ["Survey"],
        }),
        getSurveyButtonDropwdown: build.query<IResponse<StatusButton[]>, { ticket_id: string }>({
            query: params => ({
                url: `${MODULE_URL}/button_dropdown`,
                params: params,
            }),
            providesTags: (result, error, arg) => [{ type: "Survey", id: arg.ticket_id }],
        }),
        getLinkableSurveys: build.query<{ id: number; name: string }[], void>({
            query: () => ({
                url: `${MODULE_URL_2}/linkable`,
                useV3: true,
            }),
            providesTags: ["Survey"],
            transformResponse: (res: { data: { id: number; name: string }[] }) => res.data,
        }),
        linkSurvey: build.mutation<
            any,
            {
                survey_id: number
                ops_ticket_id: number
                ticket_module?: string
            }
        >({
            query: body => ({
                url: `${MODULE_URL_2}/link`,
                method: "PUT",
                body,
                useV3: true,
            }),
            invalidatesTags: ["Survey", "Installation", "Projects"],
            onQueryStarted: async (_arg, { queryFulfilled, dispatch }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message || "Survey linked successfully!", { variant: "success" })
                    dispatch(surveyService.util.invalidateTags(["Survey"]))
                } catch (error: any) {
                    enqueueSnackbar(error?.message || "Something went wrong!", { variant: "error" })
                }
            },
        }),
        dropSurveyTicket: build.query<IResponse<string>, { ticket_id: string; reason: string }>({
            query: params => ({
                url: `${MODULE_URL}/drop_ticket`,
                params,
            }),
            onQueryStarted: async (_arg, { queryFulfilled, dispatch }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.data, { variant: "success" })
                    dispatch(surveyService.util.invalidateTags(["Survey"]))
                } catch (error: any) {
                    enqueueSnackbar(error?.message, { variant: "error" })
                }
            },
        }),
        downloadSurveyTickets: build.query<IFile, { data: any }>({
            query: params => ({
                url: `${MODULE_URL_2}/download`,
                useV3: true,
                params: buildSurveyQueryParams(params.data),
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    if (typeof data === "string" || data?.urls?.download) {
                        const message = typeof data === "string" ? data : "Downloaded successfully"
                        enqueueSnackbar(message, { variant: "success" })
                    }
                } catch (error: any) {}
            },
            keepUnusedDataFor: 0,
        }),
        editSurveyReadUnreadStatus: build.mutation({
            query: body => ({
                url: `${MODULE_URL_2}/mark_read`,
                useV3: true,
                method: "POST",
                body,
            }),
            invalidatesTags: ["Survey"],
        }),
        // Mutations
        addSurveyTicket: build.mutation<IResponse<{ msg: string; ticket_id: number }>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/ticket_details`,
                method: "POST",
                body: body,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                await handleSnackbar(queryFulfilled, d => d?.data?.msg)
            },
            invalidatesTags: ["Survey"],
        }),
        editSurveyTicket: build.mutation<IResponse<string[]>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/ticket_details`,
                method: "PUT",
                body: body,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                await handleSnackbar(queryFulfilled, d => d?.data?.[0])
            },
            invalidatesTags: ["Survey", "Installation"],
        }),
        editSurveyImages: build.mutation<
            EditImageResponse,
            { data: EditImageRequest; fileData: File[]; opsId: string }
        >({
            query: body => ({
                url: `/edit_documents`,
                method: "PUT",
                body: body?.opsId
                    ? {
                          ...body?.data,
                          ticket_id: body?.opsId,
                      }
                    : body?.data,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled

                    if (data.urls.length) {
                        const responseUrl = data.urls?.[0].urls || []

                        const uploadPromises = responseUrl.map((url, index) =>
                            axios.put(url.presigned_url, arg.fileData[index], {
                                headers: {
                                    "Content-Type": arg.fileData[index].type,
                                },
                            })
                        )

                        await Promise.all(uploadPromises)

                        const finalPayload = {
                            ticket_id: arg.data.ticket_id,
                            file_infos: responseUrl.map((i: any) => ({
                                file_url: i.file_url[0],
                                report_id: i.report_id,
                                doc_id: i.doc_id,
                            })),
                        }

                        await submitFinalUploads(finalPayload)
                    }

                    dispatch(surveyService.util.invalidateTags(["Survey"]))
                } catch (error: any) {
                    enqueueSnackbar(error?.message, { variant: "error" })
                }
            },
        }),
        editSurveyTicketStatus: build.mutation<IResponse<string[]>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/ticket_put`,
                method: "PUT",
                body,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                await handleSnackbar(queryFulfilled, d => d?.data?.[0])
            },
            invalidatesTags: ["Survey", "Installation"],
        }),
        skipToStatusSurvey: build.mutation({
            query: body => ({
                url: `${MODULE_URL}/skip_to_status`,
                method: "PUT",
                body,
            }),
        }),
        editLinkToSiteSurvey: build.mutation({
            query: body => ({
                url: `${MODULE_URL}/link_survey`,
                method: "PUT",
                body,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                await handleSnackbar(queryFulfilled, d => d?.message)
            },
            invalidatesTags: (result, error, arg) => [{ type: "Survey", id: arg.get("ticket_id") as string }, "Survey"],
        }),
        updateSurveyContractor: build.mutation<
            IResponse<any>,
            { vts_id: number; vendor_representative?: string | null; contact_number?: string | null }
        >({
            query: ({ vts_id, ...body }) => ({
                url: `${MODULE_URL_2}/contractors/${vts_id}`,
                method: "PUT",
                body,
                useV3: true,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                await handleSnackbar(queryFulfilled, d => d?.data?.message, "Updated successfully")
            },
            invalidatesTags: ["Survey"],
        }),
        updateSurveyStatus: build.mutation<
            IResponse<any>,
            {
                surveyId: string | number
                ticket_id: string | number
                new_status_id: string | number
                vendor_id?: string | number
                tentative_closure_date?: string
                planned_survey_date?: string
                reason?: string
            }
        >({
            query: ({ surveyId, ...body }) => ({
                url: `${MODULE_URL_2}/${surveyId}/update_status`,
                method: "PUT",
                body: body,
                useV3: true,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                await handleSnackbar(queryFulfilled, d => d?.data?.message, "Status updated successfully")
            },
            invalidatesTags: ["Survey", "Installation", "Projects"],
        }),
        upsertSurveyTicket: build.mutation<
            any,
            {
                project_id: number
                old_survey_id: number
                new_survey_id: number
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/upsert_survey`,
                method: "PUT",
                body,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                try {
                    await queryFulfilled
                } catch (error: any) {
                    enqueueSnackbar(
                        error?.error?.status === 400
                            ? error?.error?.data?.data
                            : error?.message || "Failed to update linked survey",
                        { variant: "error" }
                    )
                }
            },
        }),

        getSurveyBOQ: build.query<any, { ticket_id: string; project_id: string; user_id?: number }>({
            query: params => ({
                url: `cristi_boq`,
                useV3: true,
                params,
            }),
            transformResponse: (response: any) => (Object.keys(response)?.length > 0 ? response : undefined),
            providesTags: [{ type: "Survey", id: "BOQ" }],
        }),

        updateSurveyTicket: build.mutation<
            any,
            {
                ticket_id: number | string
                user_id?: number | string
                planned_survey_date?: string
                reason?: string
                requested_closure_date?: string
                tentative_closure_date?: string
                add_on_ground_poc?: { name: string; contact_no: string; email?: string }
                edit_customer_poc?: { contact_id: number; name?: string; contact_no?: string; email?: string }
                add_charger?: { type: string; quantity: number; power_rating?: string; remarks?: string }[]
                edit_charger?: { charger_id: string; type?: string; power_rating?: string | number; quantity?: number }
                delete_charger?: { charger_id: string }
            }
        >({
            query: body => ({
                url: `${MODULE_URL_2}/ticket`,
                method: "PUT",
                body,
                useV3: true,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                await handleSnackbar(queryFulfilled, d => d?.message, "Updated successfully")
            },
            invalidatesTags: ["Survey"],
        }),

        addSurveyBOQ: build.mutation<
            { boq_id: number; msg: string },
            {
                project_id: number
                ticket_id: number
                reference_type: "project"
                vendor_id?: number
                module_id: number
                submodule_id: number
                items: SurveyBOQItemsPayload[]
                user_id?: number
                skipTagInvalidation?: boolean
            }
        >({
            query: ({ skipTagInvalidation, ...body }) => ({
                url: `cristi_boq`,
                useV3: true,
                method: "POST",
                body,
            }),
            invalidatesTags: (result, error, arg) => (arg.skipTagInvalidation ? [] : [{ type: "Survey", id: "BOQ" }]),
        }),
        uploadSurveyPaymentEvidence: build.mutation<any, FormData>({
            query: body => ({
                url: `${MODULE_URL_2}/finance/upload-evidence`,
                method: "POST",
                useV3: true,
                body,
            }),
        }),
        updateSurveyPaymentTerms: build.mutation<any, Omit<IPaymentDetailsPageData, "page_title">>({
            query: body => ({
                url: `${MODULE_URL_2}/finance/upsert`,
                method: "POST",
                useV3: true,
                body,
            }),
            onQueryStarted: async (_, { queryFulfilled }) => {
                try {
                    await queryFulfilled
                    enqueueSnackbar("Payment terms updated successfully", {
                        variant: "success",
                    })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Survey", "Finance"],
        }),

        // A solo survey hangs off a hidden project that never shows in the projects module, so
        // the category is edited from the ticket itself. Coupled surveys are rejected with 400 —
        // there the project is visible and owns the edit.
        updateSurveyTicketCategory: build.mutation<
            { status: string; ticket_id: number; category: { id: number; name: string } },
            { ticket_id: number | string; category_id: number }
        >({
            query: ({ ticket_id, category_id }) => ({
                url: `${MODULE_URL_2}/${ticket_id}/category`,
                method: "PUT",
                body: { category_id },
                useV3: true,
            }),
            invalidatesTags: ["Survey"],
        }),
        raiseSurveyTicket: build.mutation<
            { status: string; ticket_id: number; project_id: number; finance_id?: number },
            {
                template_id: number
                destination_site_id?: number
                accounts?: Array<{
                    account_id: number
                    account_type_id: number
                    billing_address_id?: number
                    contact_id?: number
                    contact_type_id?: number
                }>
                ticket_name: string
                category_id?: number
                business_poc_id?: number
                chargers?: Array<{
                    charger_type: string
                    quantity: number
                    power_rating?: string
                    available_load?: string
                    connection_type?: string
                    connection_owned_by?: string
                    no_of_spots?: number
                    foundation_type?: string
                    remarks?: string
                }>
                visit_by_date: string
                time_slot: number
                payment_required?: boolean
                amount?: number
                payment_terms?: Array<{ milestone: string; percentage: number }>
            }
        >({
            query: body => ({
                url: `${MODULE_URL_2}/ticket`,
                method: "POST",
                body,
                useV3: true,
            }),
            invalidatesTags: ["Survey"],
        }),
    }),
})

export const {
    useLazyGetSurveyTabsQuery,
    useLazyGetSurveyDataQuery,
    useLazyDropSurveyTicketQuery,
    useLazyDownloadSurveyTicketsQuery,

    useGetSurveyTicketDetailsQuery,
    useGetSurveyContractorsQuery,
    useGetVendorContactsQuery,
    useGetSurveyRelatedTicketsQuery,
    useGetSurveyDetailsQuery,
    useUpdateSurveyTicketCategoryMutation,
    useGetSurveyStatusDropdownQuery,
    useGetPostSurveyDetailsQuery,
    useUpdatePostSurveyDetailsMutation,
    useGetSurveyTicketHistoryQuery,
    useGetSurveyButtonDropwdownQuery,
    useGetLinkToSurveyListQuery,
    useGetSurveySalesDetailsQuery,
    useGetLinkableSurveysQuery,

    useLinkSurveyMutation,
    useAddSurveyTicketMutation,
    useEditSurveyTicketMutation,
    useEditSurveyImagesMutation,
    useEditSurveyTicketStatusMutation,
    useEditLinkToSiteSurveyMutation,
    useAddSurveyContractorMutation,
    useEditSurveyContractorMutation,
    useUpdateSurveyContractorMutation,
    useUpdateSurveyStatusMutation,
    useUpsertSurveyTicketMutation,
    useUpdateSurveyTicketMutation,
    useGetSurveyBOQQuery,
    useAddSurveyBOQMutation,
    useEditSurveyReadUnreadStatusMutation,
    useRaiseSurveyTicketMutation,
    useUploadSurveyPaymentEvidenceMutation,
    useUpdateSurveyPaymentTermsMutation,
} = surveyService

export { surveyService }
